import { useEffect, useMemo, useState } from "react";
import { Check, X, ShieldCheck, ShieldAlert } from "lucide-react";

/**
 * Sophisticated password strength meter powered by zxcvbn-ts.
 * - Scores 0-4 (Very weak to Strong) using dictionary, common patterns,
 *   keyboard sequences, repeats, and l33t-speak normalisation.
 * • Reports `crackTime` in friendly language and `feedback.suggestions`.
 * • Renders a four-segment bar + label + actionable hints.
 *
 * Library is loaded LAZILY so the heavy English wordlist (~600KB)
 * doesn't bloat the initial bundle.
 */

// Lazy singleton - load once, reuse for every keystroke.
let zxcvbnPromise = null;
function loadZxcvbn() {
  if (!zxcvbnPromise) {
    zxcvbnPromise = Promise.all([
      import("@zxcvbn-ts/core"),
      import("@zxcvbn-ts/language-common"),
      import("@zxcvbn-ts/language-en"),
    ]).then(([core, common, en]) => {
      core.zxcvbnOptions.setOptions({
        translations: en.translations,
        graphs: common.adjacencyGraphs,
        dictionary: { ...common.dictionary, ...en.dictionary },
      });
      return core.zxcvbn;
    });
  }
  return zxcvbnPromise;
}

const LEVELS = [
  { label: "Very weak", color: "bg-destructive",  text: "text-destructive" },
  { label: "Weak",      color: "bg-destructive",  text: "text-destructive" },
  { label: "Fair",      color: "bg-accent-amber", text: "text-accent-amber" },
  { label: "Good",      color: "bg-accent-green", text: "text-accent-green" },
  { label: "Strong",    color: "bg-accent-green", text: "text-accent-green" },
];

const MIN_LEN = 8;

/**
 * Whether a password meets our submit threshold. Used by parent forms
 * before posting to the backend. Threshold: zxcvbn score ≥ 2 AND length ≥ 8.
 * Fallback for the first ~100ms before zxcvbn loads: just check length + complexity.
 */
export function isPasswordAcceptable(password, score) {
  if (!password || password.length < MIN_LEN) return false;
  if (typeof score === "number") return score >= 2;
  return /[\d\W_]/.test(password);
}

export default function PasswordStrength({ password, userInputs = [], onScoreChange }) {
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!password) {
      setResult(null);
      onScoreChange?.(null);
      return;
    }
    loadZxcvbn().then((zxcvbn) => {
      if (cancelled) return;
      const r = zxcvbn(password, userInputs);
      setResult(r);
      onScoreChange?.(r.score);
    });
    return () => { cancelled = true; };
  }, [password, userInputs.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const longEnough = password.length >= MIN_LEN;

  const score = result?.score ?? 0;
  const level = LEVELS[score];
  const crackTime = result?.crackTimesDisplay?.offlineSlowHashing1e4PerSecond;
  const suggestions = result?.feedback?.suggestions || [];
  const warning = result?.feedback?.warning;

  const segments = useMemo(() => [0, 1, 2, 3], []);

  if (!password) {
    return (
      <ul className="mt-3 space-y-1 text-xs" data-testid="auth-password-rules">
        <Rule ok={false} text={`At least ${MIN_LEN} characters`} />
        <Rule ok={false} text="Avoid common words and patterns" />
      </ul>
    );
  }

  return (
    <div className="mt-3" data-testid="password-strength">
      {/* 4-segment bar */}
      <div className="flex gap-1 mb-2" aria-label="Password strength">
        {segments.map((i) => {
          const filled = i < score;
          return (
            <div
              key={i}
              data-testid={`pw-strength-seg-${i}`}
              className={`h-1 flex-1 transition-colors duration-150 ${filled ? level.color : "bg-foreground/15"}`}
            />
          );
        })}
      </div>

      {/* Label + crack time */}
      <div className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase font-mono mb-2">
        <span className={`inline-flex items-center gap-1.5 ${level.text}`} data-testid="pw-strength-label">
          {score >= 3 ? <ShieldCheck className="w-3 h-3" strokeWidth={2} /> : <ShieldAlert className="w-3 h-3" strokeWidth={2} />}
          {level.label}
        </span>
        {crackTime && (
          <span className="text-muted-foreground/70 normal-case tracking-normal">
            cracks in ~{crackTime}
          </span>
        )}
      </div>

      <ul className="space-y-1 text-xs" data-testid="auth-password-rules">
        <Rule ok={longEnough} text={`At least ${MIN_LEN} characters`} />
        <Rule
          ok={score >= 2}
          text={score >= 2 ? "Strong enough to submit" : "Make it harder to guess (avoid dictionary words / common patterns)"}
        />
      </ul>

      {/* zxcvbn feedback (warning + suggestions) */}
      {(warning || suggestions.length > 0) && score < 3 && (
        <div className="mt-2 text-[11px] text-muted-foreground leading-relaxed" data-testid="pw-strength-hint">
          {warning && <p className="text-foreground/80 mb-1">{warning}</p>}
          {suggestions.slice(0, 2).map((s, i) => (
            <p key={i} className="opacity-75">• {s}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function Rule({ ok, text }) {
  return (
    <li className={`flex items-start gap-2 ${ok ? "text-foreground/80" : "text-foreground/45"}`}>
      {ok ? <Check className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" strokeWidth={2} /> : <X className="w-3 h-3 mt-0.5 flex-shrink-0" strokeWidth={2} />}
      <span>{text}</span>
    </li>
  );
}
